import { AccountTreeOutlined, Inventory2Outlined, TaskAltOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { ReactNode } from 'react';

export type AssetsWithoutLabelsZeroStateVariant = 'no-taxonomy' | 'no-assets' | 'all-labeled';

const Panel = styled(Box)({
  flex: 1,
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '48px 24px',
});

const IconBadge = styled(Box, { shouldForwardProp: property => property !== 'tone' })<{
  tone: 'secondary' | 'success';
}>(({ theme, tone }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '14px',
  marginBottom: '16px',
  backgroundColor: alpha(theme.palette[tone].main, 0.14),
  color: theme.palette[tone].main,
  '& svg': {
    fontSize: 32,
  },
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: '4px',
  maxWidth: 420,
}));

interface ZeroStateCopy {
  icon: ReactNode;
  tone: 'secondary' | 'success';
  heading: string;
  subtitle: string;
}

const COPY: Record<AssetsWithoutLabelsZeroStateVariant, ZeroStateCopy> = {
  'no-taxonomy': {
    icon: <AccountTreeOutlined />,
    tone: 'secondary',
    heading: 'Build a taxonomy first',
    subtitle:
      'Labels come from the categories in your taxonomy. Once one exists, every asset still waiting for a label shows up here.',
  },
  'no-assets': {
    icon: <Inventory2Outlined />,
    tone: 'secondary',
    heading: 'No assets yet',
    subtitle: 'Upload assets and the ones that still need a label will collect here.',
  },
  'all-labeled': {
    icon: <TaskAltOutlined />,
    tone: 'success',
    heading: 'Every asset is labeled',
    subtitle: 'Nothing is waiting for a label. New uploads will appear here until they get one.',
  },
};

export const AssetsWithoutLabelsZeroState = ({
  variant,
  onBuildTaxonomy,
}: {
  variant: AssetsWithoutLabelsZeroStateVariant;
  onBuildTaxonomy?: () => void;
}) => {
  const { icon, tone, heading, subtitle } = COPY[variant];

  return (
    <Panel data-testid={`assets-without-labels-${variant}`}>
      <IconBadge tone={tone}>{icon}</IconBadge>
      <Heading>{heading}</Heading>
      <Subtitle>{subtitle}</Subtitle>
      {variant === 'no-taxonomy' && onBuildTaxonomy && (
        <Button variant='contained' sx={{ marginTop: '24px' }} onClick={onBuildTaxonomy}>
          Build a taxonomy
        </Button>
      )}
    </Panel>
  );
};
